import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { GlassPanel } from "../components/GlassPanel";
import { FormModal, Field, TextArea, SelectField } from "./LeadsPage";
import { Plus, Pencil, Trash2, CalendarClock, User, Package, Camera, ChevronDown, ChevronUp, Archive, MapPin, Mail, Phone, ClipboardList, Clock3, Check, Search, X } from "lucide-react";
import { toast } from "sonner";
import { useAuth, usePermission } from "../context/AuthContext";
import JobPhotos from "../components/JobPhotos";
import JobStageAssignments from "../components/JobStageAssignments";
import JobTimeline from "../components/JobTimeline";
import { summarizeSpec } from "../components/JobSpecFields";
import ContactPickerField from "../components/ContactPickerField";
import DetailsModal, { CustomerVehiclePhoto } from "../components/DetailsModal";
import StatusPipeline from "../components/StatusPipeline";
import TimelineSlotsPanel from "../components/TimelineSlotsPanel";
import PaintDepthPanel, { ptgSummary } from "../components/PaintDepthPanel";
import { useNavigate, useSearchParams } from "react-router-dom";
import { LOCATION_TYPES, jobTypeMeta, blankJobItem, normalizeItem, installationMeta, blankInstallation, visibleJobSubStatuses } from "../lib/jobTypes";
import JobTypeMultiSelect from "../components/JobTypeMultiSelect";
import JobItemForm from "../components/JobItemForm";
import { relativeTime, absoluteTime } from "../lib/utils";
import PeopleMultiSelect from "../components/PeopleMultiSelect";
import SchedulePreviewModal from "../components/SchedulePreviewModal";
import { SUB_STATUSES, subMeta } from "../lib/pipeline";

const blank = {
  contact_name: "", contact_email: "", contact_phone: "", contact_address: "", contact_id: "",
  vehicle: "", service: "", assigned_to: "", assigned_to_name: "",
  assigned_installer_ids: [], assigned_installer_names: [],
  assigned_assistant_ids: [], assigned_assistant_names: [],
  materials: [], scheduled_date: "", scheduled_end_date: "",
  main_status: "job", sub_status: "unbooked", progress: 0, notes: "",
  job_type: "car", location_type: "on_site", location_address: "",
  // Multi-item schema — mirrors Quotes so what you enter as a quote flows
  // through to the job without shape changes.
  job_items: [],
  amount: 0,
  amount_override: 0,
  spec: {},
};

const JOB_SUB_PILL = {
  unbooked: "pill-muted",
  booked: "pill-info",
  awaiting_design: "pill-warning",
  design_in_progress: "pill-warning",
  design_approved: "pill-info",
  in_store: "pill-info",
  wash_down: "pill-info",
  clay_bar: "pill-info",
  disassembly: "pill-warning",
  wrapping: "pill-warning",
  assembly: "pill-warning",
  quality_check: "pill-info",
  ready_for_collection: "pill-success",
  collected: "pill-success",
};

const ARCHIVE_PILL = {
  completed: "pill-success",
  cancelled: "pill-error",
  lost: "pill-error",
};

export default function JobsPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  // Payment chips are gated on view_invoices so a custom role that
  // turned it off doesn't leak monetary status onto job cards.
  const canViewInvoices = usePermission("view_invoices");
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [items, setItems] = useState([]);
  const [users, setUsers] = useState([]);
  const [materialsList, setMaterialsList] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(blank);
  const [materialInput, setMaterialInput] = useState("");
  const [expanded, setExpanded] = useState(null);
  const [inspectionDraft, setInspectionDraft] = useState({});
  const [detailsFor, setDetailsFor] = useState(null);
  const [tab, setTab] = useState("job"); // "job" | "archived"
  // Free-text search across contact / vehicle / service so the office can
  // pull up a specific active job in a shop with dozens on the floor.
  const [search, setSearch] = useState("");
  // Big month-grid schedule picker (opens over the job form so the office
  // can see other bookings before picking a start/end date).
  const [schedulePickerOpen, setSchedulePickerOpen] = useState(false);

  const load = async () => {
    const { data } = await api.get("/jobs", { params: { main_status: tab } });
    setItems(data);
  };

  useEffect(() => {
    load();
    api.get("/materials").then(({ data }) => setMaterialsList(data)).catch(() => setMaterialsList([]));
    // Office role also creates/edits jobs, so it needs the roster too. Use
    // the lean roster endpoint (installer + assistant only) that's already
    // scoped for office+admin.
    if (isAdmin || user?.role === "office") {
      api.get("/users/roster").then(({ data }) => setUsers(data || [])).catch(() => setUsers([]));
    }
  }, [isAdmin, user?.role, tab]);

  // Auto-open the edit modal when the URL carries ?edit=<jobId>. Used by
  // the JobDetailPage's "Edit" button so office staff can jump straight
  // into the full form without going back to the list and hunting for
  // the row. We wait until `items` has loaded so openEdit gets a hydrated
  // record (contacts, items, override, etc.), then strip the param so a
  // subsequent close+refresh doesn't re-open the modal.
  useEffect(() => {
    const editId = searchParams.get("edit");
    if (!editId || items.length === 0 || open) return;
    const target = items.find((j) => j.id === editId);
    if (target) {
      openEdit(target);
      const next = new URLSearchParams(searchParams);
      next.delete("edit");
      setSearchParams(next, { replace: true });
    }
  }, [items, searchParams]);

  const openCreate = () => { setEditing(null); setForm(blank); setOpen(true); };
  const openEdit = (it) => {
    setEditing(it);
    // Hydrate job_items from legacy single-vehicle fields if this job pre-
    // dates the multi-item schema so the office sees the same layout as
    // Quotes when editing. Do a best-effort split of "2020 Ferrari Roma" →
    // year/make/model so the values are actually EDITABLE (not dumped in a
    // notes blob that JobItemForm never renders).
    let hydratedItems = it.job_items || [];
    if (!hydratedItems.length && (it.job_type || it.vehicle || it.service)) {
      const raw = (it.vehicle || "").trim();
      const yearMatch = raw.match(/\b(19|20)\d{2}\b/);
      let hyYear = "", hyMake = "", hyModel = "";
      if (yearMatch) {
        hyYear = yearMatch[0];
        // Strip the year and split the rest into make + model.
        const rest = raw.replace(yearMatch[0], "").trim().split(/\s+/);
        if (rest.length > 0) hyMake = rest.shift() || "";
        hyModel = rest.join(" ");
      } else if (raw) {
        // No year — dump the whole string into model so it's still visible/editable.
        hyModel = raw;
      }
      hydratedItems = [{
        ...blankJobItem(it.job_type || "car"),
        year: hyYear,
        make: hyMake,
        model: hyModel,
        // Preserve the legacy service text as a visible "other" installation
        // so the office actually sees it in the edit modal (JobItemForm
        // doesn't render item-level notes). Falls back to a blank install
        // when there's no service to migrate.
        installations: it.service
          ? [{ ...blankInstallation(), installation_type: "other", notes: it.service, price: Number(it.amount) || 0 }]
          : [{ ...blankInstallation(), price: Number(it.amount) || 0 }],
        price: 0,
      }];
    }
    hydratedItems = hydratedItems.map(normalizeItem);
    // Hydrate multi-crew arrays from legacy single-installer field when the
    // job pre-dates the multi-assignment schema — makes the picker show the
    // existing installer without the user having to re-pick.
    const legacyInstallerId = it.assigned_to || "";
    const legacyInstallerName = it.assigned_to_name || "";
    const hyInstallerIds = (Array.isArray(it.assigned_installer_ids) && it.assigned_installer_ids.length > 0)
      ? it.assigned_installer_ids
      : (legacyInstallerId ? [legacyInstallerId] : []);
    const hyInstallerNames = (Array.isArray(it.assigned_installer_names) && it.assigned_installer_names.length > 0)
      ? it.assigned_installer_names
      : (legacyInstallerName ? [legacyInstallerName] : []);
    const hyAssistantIds = Array.isArray(it.assigned_assistant_ids) ? it.assigned_assistant_ids : [];
    const hyAssistantNames = Array.isArray(it.assigned_assistant_names) ? it.assigned_assistant_names : [];
    setForm({
      ...blank,
      ...it,
      job_items: hydratedItems,
      materials: it.materials || [],
      assigned_installer_ids: hyInstallerIds,
      assigned_installer_names: hyInstallerNames,
      assigned_assistant_ids: hyAssistantIds,
      assigned_assistant_names: hyAssistantNames,
    });
    setOpen(true);
  };

  const save = async (e) => {
    e.preventDefault();
    if (!form.contact_id) {
      toast.error("Please select or add a customer first");
      return;
    }
    const items = form.job_items || [];
    // Sum installation prices across all items — matches Quotes.
    const itemsSum = items.reduce((s, it) => {
      const insts = it.installations || [];
      return s + insts.reduce((ss, inst) => ss + (Number(inst.price) || 0), 0)
              + (insts.length === 0 ? (Number(it.price) || 0) : 0);
    }, 0);
    const override = Number(form.amount_override) || 0;
    const finalAmount = override > 0 ? override : (itemsSum > 0 ? itemsSum : (Number(form.amount) || 0));
    // Compose legacy mirrors so table rows / status feeds / older filters
    // still render nicely. Fall back to the current record's values when
    // the items don't yield anything meaningful — preserves legacy data
    // when the user opens/saves an unedited legacy job.
    const legacyVehicle = items
      .map((it) => [it.year, it.make, it.model].filter(Boolean).join(" ") || (it.notes || "").split("\n")[0])
      .filter(Boolean)
      .join(" · ");
    // Derive service from installation types (e.g. "Vinyl Wrap - Full
    // Colour Change + Tinting"), not from the item TYPE label. When no
    // installations are picked, preserve the original service text.
    const installLabels = Array.from(new Set(
      items.flatMap((it) => (it.installations || [])
        .map((inst) => installationMeta(inst.installation_type)?.label)
        .filter(Boolean))
    ));
    const legacyService = installLabels.length > 0 ? installLabels.join(" + ") : (form.service || "");
    const payload = {
      ...form,
      job_items: items,
      job_type: items[0]?.job_type || form.job_type || "car",
      vehicle: legacyVehicle || form.vehicle || "",
      service: legacyService,
      amount: finalAmount,
      amount_override: override,
      progress: Number(form.progress) || 0,
      main_status: "job",
      sub_status: form.sub_status || "unbooked",
    };
    if (form.assigned_to) {
      const u = users.find((x) => x.id === form.assigned_to);
      payload.assigned_to_name = u ? u.name : form.assigned_to_name;
    }
    // Multi-crew fields: derive installer/assistant lists from picked ids so
    // the names stay in sync with the ids. Also mirror the FIRST installer
    // into legacy `assigned_to` / `assigned_to_name` so older views still
    // show the "primary" installer without needing a schema migration.
    const installerPeople = (form.assigned_installer_ids || [])
      .map((id) => users.find((u) => u.id === id))
      .filter(Boolean);
    const assistantPeople = (form.assigned_assistant_ids || [])
      .map((id) => users.find((u) => u.id === id))
      .filter(Boolean);
    payload.assigned_installer_ids = installerPeople.map((u) => u.id);
    payload.assigned_installer_names = installerPeople.map((u) => u.name || u.email);
    payload.assigned_assistant_ids = assistantPeople.map((u) => u.id);
    payload.assigned_assistant_names = assistantPeople.map((u) => u.name || u.email);
    if (installerPeople.length > 0) {
      payload.assigned_to = installerPeople[0].id;
      payload.assigned_to_name = installerPeople[0].name || installerPeople[0].email;
    }
    try {
      if (editing) await api.put(`/jobs/${editing.id}`, payload);
      else await api.post("/jobs", payload);
      toast.success(editing ? "Job updated" : "Job created");
      setOpen(false);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this job?")) return;
    await api.delete(`/jobs/${id}`);
    toast.success("Job deleted");
    load();
  };

  const archive = async (job, sub = "completed") => {
    if (!window.confirm(`Move "${job.vehicle || job.contact_name}" to Archived (${sub})?`)) return;
    try {
      await api.post(`/jobs/${job.id}/advance`, { main_status: "archived", sub_status: sub });
      toast.success(`Moved to Archived — ${sub}`);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  const addMaterial = () => {
    if (!materialInput.trim()) return;
    setForm({ ...form, materials: [...form.materials, materialInput.trim()] });
    setMaterialInput("");
  };

  const removeMaterial = (i) => setForm({ ...form, materials: form.materials.filter((_, idx) => idx !== i) });

  const saveInspection = async (jobId) => {
    try {
      await api.patch(`/jobs/${jobId}/inspection`, { inspection_notes: inspectionDraft[jobId] || "" });
      toast.success("Inspection saved");
      load();
    } catch (err) { toast.error("Failed"); }
  };

  // Flip the material-ordered → material-arrived state. Called from the
  // card's tickbox. Admin/office only (backend also enforces).
  const setMaterialStatus = async (jobId, status) => {
    try {
      await api.patch(`/jobs/${jobId}/material-status`, { status });
      const label = status === "ordered" ? "Material ordered" : status === "arrived" ? "Material arrived" : "Material status cleared";
      toast.success(label);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't update material status");
    }
  };

  // Card renderer — extracted so both the "In progress" and "Not started
  // yet" sections can call the same JSX without duplicating ~180 lines.
  // `large=true` renders a wide landscape card used for the "Not started
  // yet" section and surfaces the Material Ordered → Arrived tickbox in
  // a prominent inline strip. Compact cards still show a small status
  // badge so the state is visible everywhere the job appears.
  const renderJobCard = (j, i, opts = {}) => {
    const large = !!opts.large;
    const meta = jobTypeMeta(j.job_type || "car");
    const TypeIcon = meta.icon;
    const isOffSite = j.location_type === "off_site";
    const sub = j.sub_status || (tab === "archived" ? "completed" : "unbooked");
    const pillCls = tab === "archived" ? (ARCHIVE_PILL[sub] || "pill-muted") : (JOB_SUB_PILL[sub] || "pill-muted");
    const matStatus = j.material_status || "none";
    const canEditMaterial = isAdmin || user?.role === "office";
    return (
      <GlassPanel key={j.id} className={large ? "!p-6 md:!p-7" : "!p-5"} delay={Math.min(i * 0.03, 0.4)} data-testid={`job-card-${j.id}`}>
        <div className="flex items-start justify-between mb-3">
          <button
            type="button"
            data-testid={`job-open-details-${j.id}`}
            onClick={() => navigate(`/jobs/${j.id}`)}
            className="min-w-0 text-left group flex-1"
            title="View full details"
          >
            <div className="flex items-center gap-2 text-slate-300 text-sm mb-1">
              <TypeIcon className="w-3.5 h-3.5" />
              <span>{j.vehicle}</span>
            </div>
            <div className={`font-heading ${large ? "text-2xl md:text-3xl" : "text-xl"} text-white truncate group-hover:text-white/90 group-hover:underline decoration-dotted underline-offset-4`}>
              {j.service}
            </div>
          </button>
          <div className="flex flex-col items-end gap-1.5 ml-2 shrink-0">
            <span className={`pill ${pillCls}`}>{subMeta(tab, sub).label}</span>
          </div>
        </div>
        <button
          type="button"
          data-testid={`job-contact-link-${j.id}`}
          onClick={() => j.contact_id && navigate(`/contacts?highlight=${j.contact_id}`)}
          className={`text-xs mb-3 text-left block ${j.contact_id ? "text-slate-300 hover:text-white underline decoration-dotted underline-offset-2" : "text-slate-500 cursor-default"}`}
          disabled={!j.contact_id}
          title={j.contact_id ? "Open in Contacts" : ""}
        >
          {j.contact_name}
        </button>

        {/* Customer-supplied vehicle photo — persisted from the original
            lead submission and shown at every pipeline stage so installers
            can see the exact car/bike they're prepping without opening the
            details modal. Silent when there's no photo on file. */}
        {j.photo_path && (
          <div className="mb-3" data-testid={`job-customer-photo-${j.id}`}>
            <div className="text-[9px] uppercase tracking-widest text-slate-500 mb-1">
              Customer photo
            </div>
            <CustomerVehiclePhoto path={j.photo_path} size="sm" />
          </div>
        )}

        <div className="space-y-2 text-xs text-slate-400 mb-4">
          {/* Quote-request date — when the customer first entered the
              pipeline. `quoted_at` (if backend has stamped it) beats the
              generic created_at; falls back to created_at otherwise so
              older jobs still show something useful. */}
          {(j.quoted_at || j.created_at) && (
            <div
              className="flex items-center gap-2"
              title={absoluteTime(j.quoted_at || j.created_at)}
              data-testid={`job-quoted-at-${j.id}`}
            >
              <ClipboardList className="w-3.5 h-3.5" />
              <span>Requested {relativeTime(j.quoted_at || j.created_at)}</span>
            </div>
          )}
          {j.scheduled_date && <div className="flex items-center gap-2"><CalendarClock className="w-3.5 h-3.5" />{j.scheduled_date}{j.scheduled_end_date && j.scheduled_end_date !== j.scheduled_date ? ` → ${j.scheduled_end_date}` : ""}</div>}

          {(() => {
            const installers = (j.assigned_installer_names && j.assigned_installer_names.length > 0)
              ? j.assigned_installer_names
              : (j.assigned_to_name ? [j.assigned_to_name] : []);
            const assistants = j.assigned_assistant_names || [];
            if (installers.length === 0 && assistants.length === 0) return null;
            return (
              <div className="flex flex-wrap items-center gap-x-3 gap-y-1" data-testid={`job-crew-${j.id}`}>
                {installers.length > 0 && (
                  <div className="flex items-center gap-1.5">
                    <User className="w-3.5 h-3.5" />
                    <span>{installers.join(", ")}</span>
                  </div>
                )}
                {assistants.length > 0 && (
                  <div className="flex items-center gap-1.5 text-slate-500">
                    <span className="text-[9px] uppercase tracking-widest">Asst</span>
                    <span className="text-slate-400">{assistants.join(", ")}</span>
                  </div>
                )}
              </div>
            );
          })()}
          {(j.job_type === "tinting" || j.job_type === "ppf") && summarizeSpec(j.job_type, j.spec) && (
            <div className="flex items-center gap-2">
              <span className={`pill text-[10px] ${j.job_type === "tinting" ? "pill-info" : "pill-warning"}`}>{j.job_type === "tinting" ? "TINT" : "PPF"}</span>
              <span className="truncate" data-testid={`job-spec-${j.id}`}>{summarizeSpec(j.job_type, j.spec)}</span>
            </div>
          )}
          {ptgSummary(j) && (
            <div className="flex items-center gap-2" data-testid={`job-ptg-summary-${j.id}`}>
              <span className={`pill text-[10px] ${ptgSummary(j).flagged > 0 ? "pill-warning" : "pill-info"}`}>PTG</span>
              <span className="truncate">
                {ptgSummary(j).count} reading{ptgSummary(j).count === 1 ? "" : "s"} · max {ptgSummary(j).max} µm
                {ptgSummary(j).flagged > 0 && ` · ${ptgSummary(j).flagged} flagged`}
              </span>
            </div>
          )}
          <div className="flex items-center gap-2">
            <MapPin className="w-3.5 h-3.5" />
            <span className={isOffSite ? "text-amber-300" : ""}>
              {isOffSite ? `Off-site${j.location_address ? ` — ${j.location_address}` : ""}` : "On-site (shop)"}
            </span>
          </div>
          {j.materials?.length > 0 && (
            <div className="flex items-start gap-2">
              <Package className="w-3.5 h-3.5 mt-0.5" />
              <div className="flex flex-wrap gap-1">
                {j.materials.map((m, idx) => <span key={idx} className="pill pill-muted text-[10px]">{m}</span>)}
              </div>
            </div>
          )}
        </div>

        {/* Current stage marker — a single pill-shaped status marker
            replacing the old step-circle pipeline. Shows the stage icon
            + full label + "step X of Y" progress hint so office + installers
            see at a glance where the job sits without a wall of icons.
            Clicking opens the full pipeline in Job Detail. */}
        <StageStatusMarker job={j} />

        <JobChecklist
          job={j}
          materialStatus={matStatus}
          canEditMaterial={canEditMaterial}
          canViewPayment={canViewInvoices}
          onMaterialChange={(next) => setMaterialStatus(j.id, next)}
        />

        <button
          data-testid={`job-expand-${j.id}`}
          onClick={() => setExpanded(expanded === j.id ? null : j.id)}
          className="mt-3 w-full flex items-center justify-center gap-1.5 text-xs text-slate-300 bg-white/5 hover:bg-white/10 border border-white/10 rounded-full py-1.5"
        >
          <Camera className="w-3 h-3" />
          {expanded === j.id ? "Hide details" : "Photos & inspection"}
          {expanded === j.id ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
          {(j.photos?.length || 0) > 0 && <span className="pill pill-muted text-[10px] ml-1">{j.photos.length}</span>}
        </button>

        {expanded === j.id && (
          <div className="mt-3 pt-3 border-t border-white/5 space-y-4">
            {/* Customer contact card — email / phone / address hidden from
                the collapsed card to keep the list scannable, revealed here
                for the crew that actually needs to reach the customer. */}
            {(j.contact_email || j.contact_phone || j.contact_address) && (
              <div
                className="bg-white/[0.03] border border-white/10 rounded-2xl p-3 space-y-1.5"
                data-testid={`job-customer-card-${j.id}`}
              >
                <div className="text-label flex items-center gap-1.5"><User className="w-3 h-3" /> Customer</div>
                <div className="text-sm text-white font-medium">{j.contact_name}</div>
                <div className="flex flex-wrap gap-x-3 gap-y-1 text-xs text-slate-400">
                  {j.contact_email && (
                    <a href={`mailto:${j.contact_email}`} className="flex items-center gap-1.5 hover:text-white">
                      <Mail className="w-3.5 h-3.5" />{j.contact_email}
                    </a>
                  )}
                  {j.contact_phone && (
                    <a href={`tel:${j.contact_phone}`} className="flex items-center gap-1.5 hover:text-white">
                      <Phone className="w-3.5 h-3.5" />{j.contact_phone}
                    </a>
                  )}
                </div>
                {j.contact_address && (
                  <div className="flex items-start gap-1.5 text-xs text-slate-400">
                    <MapPin className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                    <span>{j.contact_address}</span>
                  </div>
                )}
              </div>
            )}
            {(isAdmin || user?.role === "office") && (
              <JobStageAssignments
                job={j}
                canEdit={isAdmin || user?.role === "office"}
                onChange={load}
              />
            )}
            <JobHoursTracker
              job={j}
              canEdit={isAdmin || user?.role === "office" || j.assigned_to === user.id || (j.assigned_installer_ids || []).includes(user.id)}
              onSaved={load}
            />
            {isAdmin && <TimelineSlotsPanel job={j} onChange={load} />}
            <PaintDepthPanel job={j} canEdit={isAdmin || j.assigned_to === user.id} onChange={load} />
            <div className="bg-white/[0.03] border border-white/10 rounded-2xl p-3">
              <div className="text-label mb-1.5">Inspection notes</div>
              <textarea
                data-testid={`job-inspection-${j.id}`}
                value={inspectionDraft[j.id] ?? j.inspection_notes ?? ""}
                onChange={(e) => setInspectionDraft({ ...inspectionDraft, [j.id]: e.target.value })}
                rows={3}
                placeholder="Document existing damage, scratches, dents…"
                className="w-full bg-black/40 border border-white/10 rounded-xl px-3 py-2 text-sm text-white placeholder-slate-500 focus:outline-none focus:border-white/30"
              />
              <button
                data-testid={`job-save-inspection-${j.id}`}
                onClick={() => saveInspection(j.id)}
                className="mt-2 text-xs bg-white text-black px-3 py-1.5 rounded-full font-semibold hover:bg-slate-100"
              >
                Save notes
              </button>
            </div>
            <JobPhotos
              job={j}
              canEdit={isAdmin || j.assigned_to === user.id}
              onUpdate={load}
            />
            <JobTimeline
              job={j}
              canEdit={isAdmin || user?.role === "office" || j.assigned_to === user.id
                || (j.assigned_installer_ids || []).includes(user.id)
                || (j.assigned_assistant_ids || []).includes(user.id)
                || (j.timeline || []).some((s) => s.installer_id === user.id)}
              onChange={() => load()}
            />

            {/* Admin actions — moved off the outer card so they don't
                clutter the scannable list. Only surface when the details
                drawer is open. */}
            {isAdmin && (
              <div className="flex items-center justify-end gap-2 pt-3 border-t border-white/5">
                {tab === "job" && (
                  <button
                    data-testid={`job-archive-${j.id}`}
                    onClick={() => archive(j, "completed")}
                    className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full bg-white/5 hover:bg-emerald-500/20 border border-white/10 text-slate-300 hover:text-emerald-300"
                  >
                    <Archive className="w-3.5 h-3.5" /> Archive
                  </button>
                )}
                <button
                  data-testid={`job-edit-${j.id}`}
                  onClick={() => openEdit(j)}
                  className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full bg-white/5 hover:bg-white/10 border border-white/10 text-slate-300"
                >
                  <Pencil className="w-3.5 h-3.5" /> Edit
                </button>
                <button
                  data-testid={`job-delete-${j.id}`}
                  onClick={() => remove(j.id)}
                  className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full bg-white/5 hover:bg-red-500/20 border border-white/10 text-slate-300 hover:text-red-300"
                >
                  <Trash2 className="w-3.5 h-3.5" /> Delete
                </button>
              </div>
            )}
          </div>
        )}
      </GlassPanel>
    );
  };


  return (
    <div className="space-y-6">
      <div className="flex items-start md:items-center justify-between flex-wrap gap-3">
        <div className="min-w-0">
          <div className="text-label">{isAdmin ? "Workshop" : "Assigned to me"}</div>
          <h1 className="font-heading text-3xl md:text-4xl font-light tracking-tighter text-white">
            {tab === "archived" ? "Archived" : "Jobs"}
          </h1>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="inline-flex bg-white/5 border border-white/10 rounded-full p-0.5">
            <button
              data-testid="jobs-tab-active"
              onClick={() => setTab("job")}
              className={`text-xs px-3 py-1.5 rounded-full ${tab === "job" ? "bg-white text-black font-semibold" : "text-slate-300 hover:text-white"}`}
            >
              Active
            </button>
            <button
              data-testid="jobs-tab-archived"
              onClick={() => setTab("archived")}
              className={`text-xs px-3 py-1.5 rounded-full flex items-center gap-1 ${tab === "archived" ? "bg-white text-black font-semibold" : "text-slate-300 hover:text-white"}`}
            >
              <Archive className="w-3 h-3" /> Archived
            </button>
          </div>
          {isAdmin && tab === "job" && (
            <button data-testid="jobs-create-button" onClick={openCreate} className="flex items-center gap-2 bg-white text-black px-4 md:px-5 py-2.5 rounded-full text-xs md:text-sm font-semibold hover:bg-slate-100 transition">
              <Plus className="w-4 h-4" /> <span className="hidden sm:inline">Walk-in / </span>New job
            </button>
          )}
        </div>
      </div>

      {/* Search bar — filters both "In progress" and "Not started yet"
          sections (or the Archived list) by contact name, vehicle, or
          service so the office can pull up a specific job in a busy shop. */}
      <div className="relative" data-testid="jobs-search-wrap">
        <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          data-testid="jobs-search"
          placeholder={tab === "archived" ? "Search archived jobs by customer, vehicle or service…" : "Search active jobs by customer, vehicle or service…"}
          className="w-full bg-white/[0.03] border border-white/10 rounded-full pl-11 pr-10 py-2.5 text-sm text-white placeholder-slate-500 focus:outline-none focus:border-white/25 focus:bg-white/[0.05] transition"
        />
        {search && (
          <button
            type="button"
            onClick={() => setSearch("")}
            data-testid="jobs-search-clear"
            aria-label="Clear search"
            className="absolute right-3 top-1/2 -translate-y-1/2 p-1 rounded-full text-slate-400 hover:text-white hover:bg-white/10"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        )}
      </div>

      {/* Split active tab into "In progress" (something is happening on the
          workshop floor) and "Not started yet" (booked / unbooked /
          awaiting design). Archived tab keeps the single-list layout. */}
      {(() => {
        // Free-text filter applied first so both tabs and both sections
        // honour the search box. Matches contact name, vehicle description,
        // and service string case-insensitively.
        const needle = search.trim().toLowerCase();
        const matches = (j) => {
          if (!needle) return true;
          return [j.contact_name, j.vehicle, j.service]
            .filter(Boolean)
            .some((v) => String(v).toLowerCase().includes(needle));
        };
        const filtered = items.filter(matches);
        if (tab === "archived") {
          return <JobsGrid items={filtered} render={renderJobCard} emptyText={needle ? "No archived jobs match your search." : "Nothing archived yet."} isAdmin={isAdmin} />;
        }
        const inProgressSubs = new Set([
          "wash_down", "clay_bar", "disassembly", "wrapping",
          "assembly", "quality_check", "ready_to_go_home",
        ]);
        // Sort: unbooked / no-schedule jobs first (they need admin
        // attention to lock in a date), then everyone else by scheduled
        // date ascending (nearest booked first). Same rule keeps the
        // "In progress" section in the same order the crew is likely
        // to finish jobs.
        const sortForBoard = (a, b) => {
          const aUnbooked = !a.scheduled_date || a.sub_status === "unbooked";
          const bUnbooked = !b.scheduled_date || b.sub_status === "unbooked";
          if (aUnbooked !== bUnbooked) return aUnbooked ? -1 : 1;
          if (aUnbooked && bUnbooked) {
            // Same "unbooked" bucket — newest lead-arrival first so the
            // office can chase the freshest booking-request first.
            return (b.created_at || "").localeCompare(a.created_at || "");
          }
          return (a.scheduled_date || "").localeCompare(b.scheduled_date || "");
        };
        const currentJobs = filtered.filter((j) => inProgressSubs.has(j.sub_status)).sort(sortForBoard);
        // "Collected" bucket — the car has been picked up but the job is
        // still live (nothing archived yet). Lives in its own section so
        // the office can see what's ready to tidy away.
        const collectedJobs = filtered.filter((j) => j.sub_status === "collected").sort(sortForBoard);
        const upcomingJobs = filtered.filter((j) => !inProgressSubs.has(j.sub_status) && j.sub_status !== "collected").sort(sortForBoard);
        return (
          <>
            <JobsSection
              title="In progress"
              subtitle={isAdmin ? "Jobs the workshop is actively working on." : "Jobs you're actively working on."}
              items={currentJobs}
              render={renderJobCard}
              testId="jobs-section-current"
              emptyText={needle ? "No in-progress jobs match your search." : "Nothing is on the floor right now."}
            />
            <JobsSection
              title="Not started yet"
              subtitle={isAdmin ? "Booked in and waiting to be started." : "Booked in and waiting for you."}
              items={upcomingJobs}
              render={(j, i) => renderJobCard(j, i, { large: true })}
              testId="jobs-section-upcoming"
              emptyText={needle ? "No upcoming jobs match your search." : (isAdmin ? "No jobs booked in." : "No jobs assigned to you yet.")}
              large
            />
            <JobsSection
              title="Collected · ready to archive"
              subtitle="Customer has picked up the car. Archive these once the paperwork is filed."
              items={collectedJobs}
              render={renderJobCard}
              testId="jobs-section-collected"
              emptyText={needle ? "No collected jobs match your search." : "Nothing waiting to be archived."}
            />
          </>
        );
      })()}

      {open && (
        <FormModal title={editing ? "Edit job" : "New job (walk-in or manual)"} onClose={() => setOpen(false)} onSubmit={save}>
          {/* Customer — select from Contacts or add a new one */}
          <ContactPickerField
            value={form.contact_id}
            required
            testId="job-customer"
            label="Customer"
            onChange={(c) => {
              if (!c) {
                setForm({
                  ...form,
                  contact_id: "", contact_name: "", contact_email: "", contact_phone: "", contact_address: "",
                });
                return;
              }
              setForm({
                ...form,
                contact_id: c.id,
                contact_name: c.name || "",
                contact_email: c.email || "",
                contact_phone: c.phone || "",
                contact_address: c.address || "",
                vehicle: form.vehicle || c.vehicle || "",
              });
            }}
          />

          {/* Multi-item picker + per-item vehicle & installation forms — same
              structure as Quotes so what the office scoped becomes the job
              without shape changes. */}
          <JobTypeMultiSelect
            items={form.job_items || []}
            onChange={(items) => setForm({ ...form, job_items: items })}
            testId="job-job-types"
          />

          {(form.job_items || []).length > 0 && (
            <div className="space-y-3">
              {form.job_items.map((it, idx) => (
                <JobItemForm
                  key={it.id}
                  item={it}
                  index={idx}
                  onPatch={(patch) => setForm({
                    ...form,
                    job_items: form.job_items.map((x, i) => i === idx ? { ...x, ...patch } : x),
                  })}
                  onRemove={() => setForm({
                    ...form,
                    job_items: form.job_items.filter((_, i) => i !== idx),
                  })}
                />
              ))}
            </div>
          )}

          {/* Auto-sum + override total (mirrors Quotes) */}
          {(form.job_items || []).length > 0 && (() => {
            const sum = form.job_items.reduce((s, it) => {
              const insts = it.installations || [];
              return s + insts.reduce((ss, inst) => ss + (Number(inst.price) || 0), 0)
                      + (insts.length === 0 ? (Number(it.price) || 0) : 0);
            }, 0);
            return (
              <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-4 space-y-2">
                <div className="flex items-center justify-between text-sm">
                  <div className="text-[10px] uppercase tracking-widest text-slate-500">Items auto-total</div>
                  <div className="text-white font-heading text-lg">${sum.toLocaleString()}</div>
                </div>
                <div>
                  <label className="text-label block mb-1.5">Manual override (leave blank / 0 to use auto-sum)</label>
                  <input
                    type="number"
                    min="0"
                    step="1"
                    value={form.amount_override || ""}
                    onChange={(e) => setForm({ ...form, amount_override: e.target.value })}
                    placeholder="e.g. 4200 for a bundle discount"
                    data-testid="job-amount-override"
                    className="w-full bg-black/40 border border-white/10 rounded-xl px-3 py-2.5 text-sm text-white placeholder-slate-500 focus:outline-none focus:border-white/30"
                  />
                </div>
              </div>
            );
          })()}

          {/* Location */}
          <div>
            <label className="text-label block mb-2 flex items-center gap-1"><MapPin className="w-3 h-3" /> Install location</label>
            <div className="grid grid-cols-2 gap-2">
              {LOCATION_TYPES.map((l) => (
                <button
                  key={l.value}
                  type="button"
                  data-testid={`job-location-${l.value}`}
                  onClick={() => setForm({ ...form, location_type: l.value })}
                  className={`text-sm rounded-2xl border py-2 transition ${
                    form.location_type === l.value
                      ? "bg-white text-black border-white font-semibold"
                      : "bg-white/[0.03] border-white/10 text-slate-300 hover:bg-white/[0.06]"
                  }`}
                >
                  {l.label}
                </button>
              ))}
            </div>
          </div>

          {form.location_type === "off_site" && (
            <Field
              label="Off-site address"
              value={form.location_address}
              onChange={(v) => setForm({ ...form, location_address: v })}
              testId="job-address"
            />
          )}

          <div className="space-y-2">
            <div className="grid grid-cols-2 gap-3">
              <SchedulePickerButton
                label="Start date"
                value={form.scheduled_date}
                placeholder="dd/mm/yyyy"
                onOpen={() => setSchedulePickerOpen(true)}
                testId="job-date"
              />
              <SchedulePickerButton
                label="End date (optional)"
                value={form.scheduled_end_date}
                placeholder="dd/mm/yyyy"
                onOpen={() => setSchedulePickerOpen(true)}
                testId="job-end-date"
              />
            </div>
            <div className="text-[11px] text-slate-500">
              Click a date to open the shop schedule and pick around other bookings.
            </div>
          </div>
          <SelectField
            label="Status"
            value={form.sub_status}
            onChange={(v) => setForm({ ...form, sub_status: v })}
            options={SUB_STATUSES.job.map((s) => ({ value: s.value, label: s.label }))}
            testId="job-status"
          />
          <PeopleMultiSelect
            label="Assigned installers"
            testId="job-assigned-installers"
            options={users.filter((u) => u.role === "installer")}
            value={form.assigned_installer_ids || []}
            onChange={(ids, people) => setForm({
              ...form,
              assigned_installer_ids: ids,
              assigned_installer_names: people.map((p) => p.name || p.email),
              // Keep the legacy single-installer field in sync with the
              // first picked installer (drives the pipeline "My Jobs" scoping
              // + list-card avatar for older views).
              assigned_to: ids[0] || "",
              assigned_to_name: people[0] ? (people[0].name || people[0].email) : "",
            })}
            placeholder="— pick one or more installers —"
            emptyText="No installers on the roster yet"
          />
          <PeopleMultiSelect
            label="Assigned assistants (prep / disassembly / wash-down)"
            testId="job-assigned-assistants"
            options={users.filter((u) => u.role === "assistant")}
            value={form.assigned_assistant_ids || []}
            onChange={(ids, people) => setForm({
              ...form,
              assigned_assistant_ids: ids,
              assigned_assistant_names: people.map((p) => p.name || p.email),
            })}
            placeholder="— pick one or more assistants —"
            emptyText="No assistants on the roster yet"
          />
          <div>
            <label className="text-label block mb-1.5">Additional materials (job-wide)</label>
            <div className="flex gap-2 mb-2">
              <input
                data-testid="job-material-input"
                value={materialInput}
                onChange={(e) => setMaterialInput(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addMaterial())}
                placeholder="Anything not attached to a specific installation (e.g. primer, edge sealer)"
                className="flex-1 bg-black/40 border border-white/10 rounded-xl px-3 py-2 text-sm text-white placeholder-slate-500 focus:outline-none focus:border-white/30"
              />
              <button type="button" data-testid="job-material-add" onClick={addMaterial} className="px-4 py-2 rounded-xl bg-white/5 border border-white/10 text-sm text-white hover:bg-white/10">Add</button>
            </div>
            <div className="flex flex-wrap gap-1.5">
              {form.materials.map((m, i) => (
                <span key={i} className="pill pill-muted text-[10px] gap-2">
                  {m}
                  <button type="button" onClick={() => removeMaterial(i)} className="text-slate-500 hover:text-white">×</button>
                </span>
              ))}
            </div>
          </div>
          <TextArea label="Internal Shop Notes" value={form.notes} onChange={(v) => setForm({ ...form, notes: v })} testId="job-notes" hint="Internal only — customers won't see these notes." />
        </FormModal>
      )}
      {detailsFor && (
        <DetailsModal kind="job" record={detailsFor} onClose={() => setDetailsFor(null)} />
      )}
      {schedulePickerOpen && (
        <SchedulePreviewModal
          initialStart={form.scheduled_date}
          initialEnd={form.scheduled_end_date}
          excludeJobId={editing?.id || ""}
          onClose={() => setSchedulePickerOpen(false)}
          onConfirm={({ start, end }) => {
            setForm({ ...form, scheduled_date: start, scheduled_end_date: end });
            setSchedulePickerOpen(false);
          }}
        />
      )}
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Section helpers                                                            */
/* -------------------------------------------------------------------------- */


/**
 * SchedulePickerButton
 *
 * Looks and behaves like a date field (`dd/mm/yyyy`) but opens the full
 * month-grid schedule picker instead of the native browser date picker,
 * so the office can see every other booking before committing to a slot.
 */
function SchedulePickerButton({ label, value, placeholder = "dd/mm/yyyy", onOpen, testId }) {
  // Render stored YYYY-MM-DD in the local-friendly dd/mm/yyyy format
  const display = (() => {
    if (!value) return "";
    const [y, m, d] = String(value).slice(0, 10).split("-");
    if (!y || !m || !d) return value;
    return `${d}/${m}/${y}`;
  })();
  return (
    <div className="space-y-1">
      <div className="text-xs text-slate-400 uppercase tracking-wider">{label}</div>
      <button
        type="button"
        onClick={onOpen}
        data-testid={testId}
        className="w-full flex items-center justify-between gap-3 px-4 py-2.5 rounded-full bg-white/[0.03] border border-white/10 hover:border-white/25 hover:bg-white/[0.05] text-left transition"
      >
        <span className={display ? "text-white text-sm" : "text-slate-500 text-sm"}>
          {display || placeholder}
        </span>
        <CalendarClock className="w-4 h-4 text-slate-400 shrink-0" />
      </button>
    </div>
  );
}


function JobsSection({ title, subtitle, items, render, testId, emptyText, large = false }) {
  return (
    <section className="space-y-3" data-testid={testId}>
      <div className="flex items-baseline justify-between gap-3">
        <div>
          <div className="text-label">{title}</div>
          {subtitle && <div className="text-xs text-slate-500 mt-0.5">{subtitle}</div>}
        </div>
        <span className="text-[10px] uppercase tracking-widest text-slate-500">
          {items.length} {items.length === 1 ? "job" : "jobs"}
        </span>
      </div>
      <JobsGrid items={items} render={render} emptyText={emptyText} large={large} />
    </section>
  );
}

function JobsGrid({ items, render, emptyText, large = false }) {
  // Wide landscape cards for "Not started yet" so the material-ordered
  // tickbox lives in a prominent full-width strip. Keep the compact
  // 2/3-col grid for every other section so the board scans quickly.
  const gridCls = large
    ? "grid grid-cols-1 xl:grid-cols-2 gap-4"
    : "grid md:grid-cols-2 xl:grid-cols-3 gap-4";
  return (
    <div className={gridCls}>
      {items.length === 0 ? (
        <GlassPanel className="col-span-full !p-6 text-center text-slate-500 italic text-sm" delay={0.05}>
          {emptyText}
        </GlassPanel>
      ) : (
        items.map((j, i) => render(j, i))
      )}
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Payment chip                                                               */
/* -------------------------------------------------------------------------- */
/* -------------------------------------------------------------------------- */
/* StageStatusMarker                                                          */
/* -------------------------------------------------------------------------- */
/**
 * One prominent pill-shaped marker showing the current stage of a job
 * (replaces the old row of 10 step-circles that used to live on each
 * card). Keeps the card scannable: stage icon + stage label + "step X
 * of Y" progress hint. Clicking opens the Job Detail page where the
 * full interactive pipeline lives.
 *
 * Visual: colour-matched to the sub-status pill at the top-right of the
 * card so the two markers reinforce each other rather than competing.
 */
function StageStatusMarker({ job }) {
  const navigate = useNavigate();
  const main = job.main_status || "job";
  const sub = job.sub_status || "";
  const allSteps = SUB_STATUSES[main] || [];
  // Match the pipeline's visibility filter so the step counter reflects
  // only the stages that actually apply to this job's installations.
  const steps = main === "job"
    ? (() => {
        const visible = new Set(visibleJobSubStatuses(job));
        if (sub) visible.add(sub);
        return allSteps.filter((s) => visible.has(s.value));
      })()
    : allSteps;
  const idx = steps.findIndex((s) => s.value === sub);
  if (idx < 0) return null;
  const step = steps[idx];
  const Icon = step.icon;
  const total = steps.length;
  const num = idx + 1;
  return (
    <button
      type="button"
      onClick={() => navigate(`/jobs/${job.id}`)}
      data-testid={`job-stage-marker-${job.id}`}
      title={`${step.label} — step ${num} of ${total}. Click to open the full pipeline.`}
      className="mt-1 w-full flex items-center gap-3 rounded-2xl border border-white/10 bg-white/[0.04] hover:bg-white/[0.07] hover:border-white/20 px-4 py-2.5 transition group"
    >
      {/* Mini stepper dots — one per visible stage, filled up to current.
          Keeps the "where are we" at-a-glance signal without reintroducing
          the full labelled row of circles. */}
      <div className="flex items-center gap-1 shrink-0">
        {steps.map((s, i) => (
          <span
            key={s.value}
            className={`block rounded-full transition ${
              i < idx ? "w-1.5 h-1.5 bg-white/40"
              : i === idx ? "w-2.5 h-2.5 bg-white"
              : "w-1.5 h-1.5 bg-white/10"
            }`}
          />
        ))}
      </div>
      <div className="flex items-center gap-2 min-w-0 flex-1">
        <Icon className="w-4 h-4 text-slate-300 shrink-0" />
        <span className="text-sm font-medium text-white truncate">{step.label}</span>
      </div>
      <span className="text-[10px] uppercase tracking-widest text-slate-500 shrink-0">
        Step {num}/{total}
      </span>
    </button>
  );
}


/* JobChecklist — thin tick/cross rows summarising job readiness on each card. */
const CHECK_TONE = {
  done:    { cls: "bg-emerald-500 text-white border-emerald-500", Icon: Check },
  partial: { cls: "bg-sky-500 text-white border-sky-500", Icon: Clock3 },
  missing: { cls: "bg-rose-50 text-rose-600 border-rose-300 dark:bg-rose-500/10 dark:text-rose-300 dark:border-rose-400/40", Icon: X },
};

function ChecklistRow({ state, label, detail, onClick, onReset, testId, title }) {
  const { cls, Icon } = CHECK_TONE[state];
  const Tag = onClick ? "button" : "div";
  return (
    <div className="flex items-center gap-2.5 px-3 py-2 text-xs" data-testid={testId} data-state={state} title={title}>
      <Tag
        {...(onClick ? { type: "button", onClick, "data-testid": `${testId}-toggle` } : {})}
        className={`w-4 h-4 shrink-0 rounded-full border flex items-center justify-center ${cls} ${onClick ? "hover:scale-110 transition-transform" : ""}`}
      >
        <Icon className="w-2.5 h-2.5" strokeWidth={3} />
      </Tag>
      <span className={`flex-1 min-w-0 truncate ${state === "done" ? "text-slate-200" : "text-white font-medium"}`}>{label}</span>
      {detail && <span className="text-[10px] text-slate-500 shrink-0 truncate max-w-[45%]">{detail}</span>}
      {onReset && (
        <button type="button" onClick={onReset} data-testid={`${testId}-reset`}
          className="text-[9px] uppercase tracking-widest text-slate-500 hover:text-white shrink-0">
          Undo
        </button>
      )}
    </div>
  );
}

function JobChecklist({ job, materialStatus, canEditMaterial, canViewPayment, onMaterialChange }) {
  const money = (n) => `$${Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
  const order = visibleJobSubStatuses(job);
  const subIdx = order.indexOf(job.sub_status || "");
  const reached = (stage) => order.includes(stage) && subIdx >= order.indexOf(stage);
  const isJob = (job.main_status || "job") === "job";
  const rows = [];

  rows.push(job.scheduled_date
    ? { key: "booked", state: "done", label: "Booked in", detail: job.scheduled_date }
    : { key: "booked", state: "missing", label: "Not booked in yet" });

  const crew = (job.assigned_installer_names?.length || 0) + (job.assigned_to_name && !job.assigned_installer_names?.length ? 1 : 0);
  rows.push(crew > 0
    ? { key: "crew", state: "done", label: "Crew assigned", detail: `${crew} installer${crew === 1 ? "" : "s"}` }
    : { key: "crew", state: "missing", label: "No crew assigned" });

  if (order.includes("awaiting_design")) {
    const ready = reached("design_approved");
    const inProg = job.sub_status === "design_in_progress";
    rows.push({ key: "design", state: ready ? "done" : inProg ? "partial" : "missing",
      label: ready ? "Design ready" : inProg ? "Design in progress" : "Design not started" });
  }

  const matNext = materialStatus === "none" ? "ordered" : "arrived";
  rows.push({
    key: "material",
    state: materialStatus === "arrived" ? "done" : materialStatus === "ordered" ? "partial" : "missing",
    label: materialStatus === "arrived" ? "Material arrived" : materialStatus === "ordered" ? "Material ordered — awaiting arrival" : "Material not ordered",
    detail: materialStatus === "arrived" && job.material_arrived_at ? relativeTime(job.material_arrived_at)
      : materialStatus === "ordered" && job.material_ordered_at ? relativeTime(job.material_ordered_at) : "",
    onClick: canEditMaterial && materialStatus !== "arrived" ? () => onMaterialChange(matNext) : null,
    onReset: canEditMaterial && materialStatus !== "none" ? () => {
      if (window.confirm(materialStatus === "arrived" ? "Mark material as not-yet-arrived?" : "Clear the material-ordered tick?")) {
        onMaterialChange(materialStatus === "arrived" ? "ordered" : "none");
      }
    } : null,
    title: canEditMaterial && materialStatus !== "arrived" ? (materialStatus === "none" ? "Tick once ordered" : "Tick once arrived") : "",
  });

  if (isJob) {
    rows.push(job.terms_signed_at
      ? { key: "terms", state: "done", label: "T&Cs signed", detail: job.terms_signed_name || "", title: new Date(job.terms_signed_at).toLocaleString() }
      : { key: "terms", state: "missing", label: "T&Cs not signed" });
  }

  const inspPhotos = (job.photos || []).filter((p) => p.category === "pre_inspection").length;
  const inspected = inspPhotos > 0 || !!(job.inspection_notes || "").trim();
  rows.push(inspected
    ? { key: "inspection", state: "done", label: "Vehicle inspected", detail: inspPhotos ? `${inspPhotos} photo${inspPhotos === 1 ? "" : "s"}` : "notes only" }
    : { key: "inspection", state: "missing", label: "Vehicle not inspected" });

  const ps = job.payment_summary;
  if (canViewPayment && ps && ps.status !== "no_invoice") {
    rows.push(ps.status === "paid"
      ? { key: "payment", state: "done", label: "Paid in full", detail: money(ps.paid) }
      : ps.status === "deposit"
      ? { key: "payment", state: "partial", label: "Deposit received", detail: `${money(ps.paid)} / ${money(ps.billed)}` }
      : { key: "payment", state: "missing", label: "Unpaid", detail: `${money(ps.billed)} billed` });
  }

  const done = rows.filter((r) => r.state === "done").length;
  return (
    <div className="mt-3 rounded-2xl border border-white/10 bg-white/[0.02] overflow-hidden" data-testid={`job-checklist-${job.id}`}>
      <div className="flex items-center justify-between px-3 py-1.5 bg-white/[0.03] border-b border-white/10">
        <span className="text-[9px] uppercase tracking-widest text-slate-500">Job readiness</span>
        <span className="text-[10px] text-slate-400" data-testid={`job-checklist-count-${job.id}`}>{done}/{rows.length}</span>
      </div>
      <div className="divide-y divide-white/[0.06]">
        {rows.map((r) => (
          <ChecklistRow key={r.key} {...r} testId={`job-check-${r.key}-${job.id}`} />
        ))}
      </div>
    </div>
  );
}


/**
 * Small visual for the installer — shows total estimated hours, a
 * progress bar tied to job.progress, and "N/M hrs" so they know how far
 * through the booked window they are. Silent when no hours have been
 * estimated so legacy jobs stay clean.
 */
function InstallTimeGuide({ job }) {
  const items = Array.isArray(job.job_items) ? job.job_items : [];
  const totalHours = items.reduce((sum, it) => {
    const installs = it.installations || [];
    return sum + installs.reduce((s, i) => s + (Number(i.estimated_hours) || 0), 0);
  }, 0);
  if (totalHours <= 0) return null;

  const pct = Math.max(0, Math.min(100, Number(job.progress) || 0));
  const doneHours = (totalHours * pct) / 100;
  return (
    <div className="pt-1" data-testid={`job-install-time-${job.id}`}>
      <div className="flex items-center justify-between text-[10px] uppercase tracking-widest text-slate-500 mb-1">
        <span>Install time</span>
        <span data-testid={`job-install-time-value-${job.id}`}>
          {doneHours.toFixed(1)} / {totalHours} hrs
        </span>
      </div>
      <div className="h-1.5 rounded-full bg-white/[0.06] overflow-hidden">
        <div
          className={`h-full transition-all ${
            pct >= 100 ? "bg-emerald-400" : pct >= 60 ? "bg-amber-400" : "bg-cyan-400"
          }`}
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  );
}


/* -------------------------------------------------------------------------- */
/* JobHoursTracker                                                            */
/* -------------------------------------------------------------------------- */
/**
 * Editable "expected vs actual" hours panel. One row per installation on
 * the job. Estimated hours came from the quote form; actual hours are
 * logged here as the crew works. Also feeds the auto-quoter's effective
 * hourly-rate calculation — see backend `quote_agent._build_pricing_summary`.
 */
function JobHoursTracker({ job, canEdit, onSaved }) {
  const [items, setItems] = React.useState(job.job_items || []);
  const [materialsNotes, setMaterialsNotes] = React.useState(job.materials_used_notes || "");
  const [saving, setSaving] = React.useState(false);
  const [dirty, setDirty] = React.useState(false);

  React.useEffect(() => {
    // Only re-hydrate local state from the parent's job snapshot when
    // the user isn't in the middle of typing. Otherwise a background
    // refresh (parent-level `load()` triggered by a sibling like the
    // Pipeline advance button, a stage-installer save, or the
    // Inspection notes panel) would blow away the user's in-progress
    // notes and flash the "Required" pill back on for a beat before
    // the next render catches up.
    if (dirty) return;
    setItems(job.job_items || []);
    setMaterialsNotes(job.materials_used_notes || "");
  }, [job.id, job.updated_at, dirty]);

  const flat = React.useMemo(() => {
    const rows = [];
    (items || []).forEach((it, itemIdx) => {
      const vehicle = [it.year, it.make, it.model].filter(Boolean).join(" ") || it.item_description || `Item ${itemIdx + 1}`;
      (it.installations || []).forEach((inst, instIdx) => {
        rows.push({ itemIdx, instIdx, vehicle, inst });
      });
    });
    return rows;
  }, [items]);

  if (flat.length === 0) return null;

  const totals = flat.reduce((t, r) => ({
    est: t.est + (Number(r.inst.estimated_hours) || 0),
    act: t.act + (Number(r.inst.actual_hours) || 0),
  }), { est: 0, act: 0 });
  const variance = totals.act - totals.est;
  const variancePct = totals.est > 0 ? Math.round((variance / totals.est) * 100) : 0;

  const patchRow = (itemIdx, instIdx, field, value) => {
    if (!canEdit) return;
    setItems((prev) => prev.map((it, i) => i !== itemIdx ? it : {
      ...it,
      installations: (it.installations || []).map((inst, j) => j !== instIdx ? inst : { ...inst, [field]: Number(value) || 0 }),
    }));
    setDirty(true);
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/jobs/${job.id}`, { ...job, job_items: items, materials_used_notes: materialsNotes });
      toast.success("Hours & materials saved");
      setDirty(false);
      onSaved?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      data-testid={`job-hours-tracker-${job.id}`}
      className="bg-white/[0.03] border border-white/10 rounded-2xl p-3 space-y-3"
    >
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div>
          <div className="text-label flex items-center gap-1.5">
            <Clock3 className="w-3 h-3" /> Hours tracker
          </div>
          <div className="text-[10px] text-slate-500 mt-0.5">
            Log real install time so the auto-quoter learns from what jobs actually take
          </div>
        </div>
        <div className="flex items-center gap-3 text-[11px]">
          <div className="text-slate-400">
            Est. <span className="text-white font-medium">{totals.est.toFixed(1)}h</span>
          </div>
          <div className="text-slate-400">
            Actual <span className="text-white font-medium">{totals.act.toFixed(1)}h</span>
          </div>
          {totals.act > 0 && totals.est > 0 && (
            <div
              data-testid={`job-hours-variance-${job.id}`}
              className={`px-2 py-0.5 rounded-full font-semibold ${
                variance <= 0 ? "bg-emerald-500/15 text-emerald-200 border border-emerald-400/30"
                : variancePct <= 15 ? "bg-amber-500/15 text-amber-200 border border-amber-400/30"
                : "bg-red-500/15 text-red-200 border border-red-400/30"
              }`}
            >
              {variance > 0 ? "+" : ""}{variance.toFixed(1)}h ({variancePct > 0 ? "+" : ""}{variancePct}%)
            </div>
          )}
        </div>
      </div>

      <div className="rounded-xl bg-black/25 border border-white/5 divide-y divide-white/5">
        {flat.map(({ itemIdx, instIdx, vehicle, inst }) => {
          const meta = installationMeta(inst.installation_type);
          const rowKey = `${itemIdx}-${instIdx}`;
          const est = Number(inst.estimated_hours) || 0;
          const act = Number(inst.actual_hours) || 0;
          const rowVariance = act - est;
          return (
            <div
              key={rowKey}
              data-testid={`job-hours-row-${job.id}-${itemIdx}-${instIdx}`}
              className="px-3 py-2.5 flex items-center gap-3 flex-wrap"
            >
              <div className="min-w-0 flex-1">
                <div className="text-xs text-white truncate">{vehicle}</div>
                <div className="text-[10px] uppercase tracking-widest text-slate-500 truncate">
                  {meta?.label || inst.installation_type || "installation"}
                </div>
              </div>
              <div className="flex items-center gap-1.5">
                <span className="text-[10px] uppercase tracking-widest text-slate-500">Est</span>
                <input
                  type="number"
                  min="0"
                  step="0.5"
                  disabled={!canEdit}
                  value={est || ""}
                  placeholder="0"
                  onChange={(e) => patchRow(itemIdx, instIdx, "estimated_hours", e.target.value)}
                  data-testid={`job-hours-est-${job.id}-${itemIdx}-${instIdx}`}
                  className="w-16 bg-black/40 border border-white/10 rounded-lg px-2 py-1 text-xs text-white placeholder-slate-500 text-right focus:outline-none focus:border-white/30 disabled:opacity-60"
                />
                <span className="text-[10px] text-slate-500">h</span>
              </div>
              <div className="flex items-center gap-1.5">
                <span className="text-[10px] uppercase tracking-widest text-slate-500">Actual</span>
                <input
                  type="number"
                  min="0"
                  step="0.25"
                  disabled={!canEdit}
                  value={act || ""}
                  placeholder="0"
                  onChange={(e) => patchRow(itemIdx, instIdx, "actual_hours", e.target.value)}
                  data-testid={`job-hours-actual-${job.id}-${itemIdx}-${instIdx}`}
                  className="w-16 bg-black/40 border border-emerald-400/25 rounded-lg px-2 py-1 text-xs text-white placeholder-slate-500 text-right focus:outline-none focus:border-emerald-400/60 disabled:opacity-60"
                />
                <span className="text-[10px] text-slate-500">h</span>
              </div>
              {act > 0 && est > 0 && (
                <div className={`text-[10px] font-semibold w-14 text-right ${
                  rowVariance <= 0 ? "text-emerald-300"
                  : rowVariance / est <= 0.15 ? "text-amber-300"
                  : "text-red-300"
                }`}>
                  {rowVariance > 0 ? "+" : ""}{rowVariance.toFixed(1)}h
                </div>
              )}
            </div>
          );
        })}
      </div>

      {canEdit && (
        <div className="space-y-1 border-t border-white/5 pt-3">
          <label className="text-label flex items-center justify-between gap-2">
            <span className="flex items-center gap-1.5">
              <Package className="w-3 h-3" /> Estimated material used
            </span>
            {(job.sub_status === "wrapping" && !materialsNotes.trim() && !dirty) && (
              <span className="text-[10px] text-amber-300 normal-case tracking-normal" data-testid={`job-materials-required-${job.id}`}>
                Required before advancing past wrapping
              </span>
            )}
          </label>
          <textarea
            value={materialsNotes}
            onChange={(e) => { setMaterialsNotes(e.target.value); setDirty(true); }}
            placeholder="e.g. ~12m of 3M 2080 Deep Black + 2 sheets XPEL PPF"
            rows={2}
            data-testid={`job-materials-used-${job.id}`}
            className="w-full bg-black/40 border border-white/10 rounded-xl px-3 py-2 text-xs text-white placeholder-slate-500 focus:outline-none focus:border-white/30 resize-none"
          />
        </div>
      )}
      {!canEdit && job.materials_used_notes && (
        <div className="space-y-1 border-t border-white/5 pt-3">
          <div className="text-label flex items-center gap-1.5"><Package className="w-3 h-3" /> Material used</div>
          <div className="text-xs text-slate-300 whitespace-pre-wrap">{job.materials_used_notes}</div>
        </div>
      )}

      {canEdit && (
        <div className="flex items-center justify-end gap-2">
          {dirty && <span className="text-[10px] text-amber-300">Unsaved changes</span>}
          <button
            type="button"
            disabled={!dirty || saving}
            onClick={save}
            data-testid={`job-hours-save-${job.id}`}
            className="text-xs bg-white text-black px-3 py-1.5 rounded-full font-semibold hover:bg-slate-100 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {saving ? "Saving…" : "Save"}
          </button>
        </div>
      )}
    </div>
  );
}
